import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../ui/Button';
import analyticsService from '../../../utils/analyticsService';
import OverviewMetricCard from './OverviewMetricCard';
import TrendChart from './TrendChart';
import GoalProgress from './GoalProgress';
import SkillAnalysis from './SkillAnalysisSimple';
import FeedbackItem from './FeedbackItem';
import { LoadingSpinner, ErrorMessage } from './StateComponents';
import TimeRangeSelector from './TimeRangeSelector';

const AnalyticsDashboard = () => {
  const { userProfile } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, 1y
  const [error, setError] = useState(null);

  // Fetch real analytics data from Supabase
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!userProfile?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all required data in parallel
        const [
          userAnalytics,
          applicationTrends,
          skillsAnalytics,
          recentFeedback,
          userGoals
        ] = await Promise.all([
          analyticsService.getUserAnalytics(userProfile.id),
          analyticsService.getApplicationTrends(userProfile.id, timeRange),
          analyticsService.getSkillsAnalytics(userProfile.id),
          analyticsService.getRecentFeedback(userProfile.id),
          analyticsService.getUserGoals(userProfile.id)
        ]);
        
        // Combine all data into the format expected by the component
        const formattedData = {
          overview: {
            totalApplications: userAnalytics.totalApplications || 0,
            responseRate: userAnalytics.responseRate || 0,
            interviewRate: userAnalytics.interviewRate || 0,
            successRate: userAnalytics.successRate || 0,
            averageRating: userAnalytics.averageRating || 0,
            totalEarnings: userAnalytics.totalEarnings || 0
          },
          trends: {
            applications: applicationTrends.applications || [],
            responses: applicationTrends.responses || []
          },
          topSkills: Array.isArray(skillsAnalytics) ? skillsAnalytics : [],
          recentFeedback: Array.isArray(recentFeedback) ? recentFeedback : [],
          goals: userGoals.map(goal => ({
            id: goal.id,
            title: goal.title,
            current: goal.currentValue,
            target: goal.targetValue,
            progress: goal.progress
          })) || []
        };
        
        setAnalyticsData(formattedData);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [userProfile?.id, timeRange]);

  // Memoized utility functions
  const utils = useMemo(() => ({
    formatCurrency: (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    },
    
    formatDate: (date) => {
      return new Date(date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    getProgressColor: (progress) => {
      if (progress >= 90) return 'bg-green-500';
      if (progress >= 70) return 'bg-blue-500';
      if (progress >= 50) return 'bg-yellow-500';
      return 'bg-red-500';
    }
  }), []);

  // Memoized overview metrics configurations
  const metricsConfig = useMemo(() => {
    if (!analyticsData) return [];
    
    return [
      {
        label: 'Applications',
        value: analyticsData.overview.totalApplications,
        valueClassName: 'text-gray-900'
      },
      {
        label: 'Response Rate',
        value: `${analyticsData.overview.responseRate}%`,
        valueClassName: 'text-blue-600'
      },
      {
        label: 'Interview Rate',
        value: `${analyticsData.overview.interviewRate}%`,
        valueClassName: 'text-green-600'
      },
      {
        label: 'Success Rate',
        value: `${analyticsData.overview.successRate}%`,
        valueClassName: 'text-purple-600'
      },
      {
        label: 'Avg Rating',
        value: `⭐ ${analyticsData.overview.averageRating}`,
        valueClassName: 'text-yellow-600'
      },
      {
        label: 'Total Earnings',
        value: utils.formatCurrency(analyticsData.overview.totalEarnings),
        valueClassName: 'text-green-600'
      }
    ];
  }, [analyticsData, utils]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={() => window.location.reload()} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600">Track your performance and growth</p>
        </div>
        
        {/* Time Range Selector */}
        <TimeRangeSelector 
          selectedRange={timeRange} 
          onRangeChange={setTimeRange} 
        />
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metricsConfig.map((metric, index) => (
          <OverviewMetricCard 
            key={index}
            label={metric.label}
            value={metric.value}
            valueClassName={metric.valueClassName}
          />
        ))}
      </div>

      {/* Charts and Goals Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Simple Trend Chart */}
        <TrendChart 
          title="Application Trends"
          data={analyticsData.trends.applications.slice(-7)}
          formatDate={utils.formatDate}
        />

        {/* Goals Progress */}
        <GoalProgress 
          goals={analyticsData.goals}
          getProgressColor={utils.getProgressColor}
        />
      </div>

      {/* Skills Analysis and Recent Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Skills */}
        <SkillAnalysis 
          skills={analyticsData.topSkills}
        />

        {/* Recent Feedback */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Feedback</h3>
          <div className="space-y-4">
            {analyticsData.recentFeedback.length > 0 ? (
              analyticsData.recentFeedback.map(feedback => (
                <FeedbackItem 
                  key={feedback.id}
                  feedback={feedback}
                  formatDate={utils.formatDate}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No feedback received yet</p>
                <p className="text-xs mt-1">Complete some jobs to start receiving feedback from employers</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {/* <div className="flex flex-wrap gap-4">
        <Button variant="outline">📊 Export Report</Button>
        <Button variant="outline">🎯 Set New Goals</Button>
        <Button variant="outline">📈 View Detailed Analytics</Button>
      </div> */}
    </div>
  );
};

export default AnalyticsDashboard;
